import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Search, Pencil, Trash2, Download, Building2, Eye, Upload, ChevronsUpDown } from "lucide-react";
import { api, useAuth, formatApiError, downloadFile, canDo } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import StatutBadge from "../components/StatutBadge";
import EmployeeDialog from "../components/EmployeeDialog";
import ExportDialog from "../components/ExportDialog";
import ServicesDialog from "../components/ServicesDialog";
import EmployeeDetail from "../components/EmployeeDetail";
import ImportDialog from "../components/ImportDialog";
import { toast } from "sonner";

const STATUT_OPTIONS = [
  { value: "valide", label: "Valide (inclut « à recycler »)" },
  { value: "a_recycler", label: "À recycler" },
  { value: "depasse", label: "Dépassé" },
  { value: "non_forme", label: "Non formé" },
];

const CAT_OPTIONS = [
  { value: "sst", label: "SST" },
  { value: "ssiap", label: "SSIAP" },
  { value: "carte_pro", label: "Cartes Pro" },
  { value: "incendie", label: "Formations Incendie" },
];

function MultiFilter({ label, options, selected, onChange, testid }) {
  const toggle = (v) => onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-full sm:w-auto sm:min-w-[210px] justify-between bg-card font-normal" data-testid={testid}>
          <span className="truncate">{selected.length ? `${label.replace(/^Tous les |^Tous /, "")} (${selected.length})` : label}</span>
          <ChevronsUpDown className="h-4 w-4 ml-2 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-2" align="start">
        <div className="max-h-64 overflow-y-auto space-y-0.5">
          {options.map((o) => (
            <label key={o.value} className="flex items-center gap-2.5 rounded-md px-2 py-2 hover:bg-secondary/60 cursor-pointer">
              <Checkbox checked={selected.includes(o.value)} onCheckedChange={() => toggle(o.value)} data-testid={`${testid}-option-${o.value.replace(/[^a-zA-Z0-9]+/g, "-")}`} />
              <span className="text-sm">{o.label}</span>
            </label>
          ))}
        </div>
        {selected.length > 0 && (
          <Button variant="ghost" size="sm" className="w-full mt-1" onClick={() => onChange([])} data-testid={`${testid}-clear`}>Tout effacer</Button>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default function Employees() {
  const { user } = useAuth();
  const readOnly = !canDo(user, "sst", "modifier");
  const [employees, setEmployees] = useState([]);
  const [services, setServices] = useState([]);
  const [search, setSearch] = useState("");
  const [servicesSel, setServicesSel] = useState([]);
  const [searchParams] = useSearchParams();
  const [catSel, setCatSel] = useState(() => searchParams.get("cat") || "sst");
  const [statutsSel, setStatutsSel] = useState(() => {
    const s = searchParams.get("statut");
    return s ? s.split(",").filter(Boolean) : [];
  });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [detailEmp, setDetailEmp] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (servicesSel.length) params.service = servicesSel.join(",");
    if (statutsSel.length) params.statut = statutsSel.join(",");
    params.cat = catSel;
    Promise.all([api.get("/employees", { params }), api.get("/services")])
      .then(([r, s]) => { setEmployees(r.data.employees); setServices(s.data.services); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [search, servicesSel, statutsSel, catSel]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const remove = async (emp) => {
    if (!window.confirm(`Supprimer ${emp.prenom} ${emp.nom} ? Cette action est définitive.`)) return;
    try {
      await api.delete(`/employees/${emp.id}`);
      toast.success(`${emp.prenom} ${emp.nom} supprimé`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="employees-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-accent mb-1">Gestion du personnel</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ fontFamily: "Outfit" }}>Salariés</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          {!readOnly && (
            <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-excel-button">
              <Upload className="h-4 w-4 mr-2" /> Import Excel
            </Button>
          )}
          {!readOnly && (
            <Button variant="outline" onClick={() => setServicesOpen(true)} data-testid="manage-services-button">
              <Building2 className="h-4 w-4 mr-2" /> Gérer les services
            </Button>
          )}
          <ExportDialog type="sst" filename="export_sst.xlsx" testid="export-excel-button"
            extraParams={servicesSel.length ? `&service=${encodeURIComponent(servicesSel.join(","))}` : ""}
            initialStatuts={statutsSel.length ? [...new Set(statutsSel.flatMap((s) => (s === "valide" ? ["valide", "a_recycler"] : [s])))] : undefined} />
          {!readOnly && (
            <Button onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="add-employee-button">
              <Plus className="h-4 w-4 mr-2" /> Ajouter un salarié
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Rechercher un nom..." className="pl-9 bg-card" value={search}
            onChange={(e) => setSearch(e.target.value)} data-testid="employee-search-input" />
        </div>
        <MultiFilter label="Tous les services" testid="employee-filter-service"
          options={services.map((s) => ({ value: s.nom, label: `${s.nom} — ${s.nb_sst} SST / ${s.nb_salaries} salariés` }))}
          selected={servicesSel} onChange={setServicesSel} />
        <Select value={catSel} onValueChange={setCatSel}>
          <SelectTrigger className="w-full sm:w-[200px] bg-card" data-testid="employee-filter-categorie"><SelectValue /></SelectTrigger>
          <SelectContent>
            {CAT_OPTIONS.map((c) => <SelectItem key={c.value} value={c.value} data-testid={`employee-cat-${c.value}`}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <MultiFilter label={`Tous les statuts (${CAT_OPTIONS.find((c) => c.value === catSel)?.label})`} testid="employee-filter-statut"
          options={STATUT_OPTIONS} selected={statutsSel} onChange={setStatutsSel} />
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="employee-table">
            <thead>
              <tr className="border-b border-border text-left text-xs font-mono uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3">Nom Prénom</th>
                <th className="px-4 py-3">Date de naissance</th>
                <th className="px-4 py-3">Service</th>
                <th className="px-4 py-3">Formation SST</th>
                <th className="px-4 py-3">Fin de validité</th>
                <th className="px-4 py-3">Statut</th>
                <th className="px-4 py-3">MAC proposée</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">Chargement...</td></tr>
              )}
              {!loading && employees.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground" data-testid="employee-empty">Aucun salarié trouvé.</td></tr>
              )}
              {!loading && employees.map((e) => (
                <tr key={e.id} className="border-b border-border/50 hover:bg-secondary/40 transition-colors" data-testid={`employee-row-${e.id}`}>
                  <td className="px-4 py-3">
                    <span className="font-medium">{e.nom}</span> {e.prenom}
                    {e.ssiap?.length > 0 && (
                      <span className="ml-2 inline-flex rounded-full border border-red-700 bg-red-950 px-2 py-0.5 text-[10px] font-bold text-red-400" data-testid={`ssiap-badge-${e.id}`}>
                        {e.ssiap.map((s) => `SSIAP ${s.niveau}`).join(" · ")}
                      </span>
                    )}
                    {e.carte_pro && (
                      <span className="ml-2 inline-flex rounded-full border border-orange-700 bg-orange-950 px-2 py-0.5 text-[10px] font-bold text-orange-400" data-testid={`cartepro-badge-${e.id}`}>
                        Carte Pro
                      </span>
                    )}
                    {e.formation_incendie && (
                      <span className="ml-2 inline-flex rounded-full border border-sky-700 bg-sky-950 px-2 py-0.5 text-[10px] font-bold text-sky-400" title="Équipier de Première Intervention" data-testid={`incendie-badge-${e.id}`}>
                        EPI
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{e.naissance || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{e.service}</td>
                  <td className="px-4 py-3 font-mono text-xs">{e.date_formation || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs">{e.fin_validite || "—"}</td>
                  <td className="px-4 py-3"><StatutBadge statut={e.statut} joursRestants={e.jours_restants} /></td>
                  <td className="px-4 py-3 font-mono text-xs">
                    {e.proposition_mac ? <>{e.proposition_mac}{e.valide_mac === "Oui" && <span className="ml-1 text-emerald-400">✓</span>}</> : "—"}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Button variant="ghost" size="icon" onClick={() => setDetailEmp(e)} data-testid={`detail-employee-${e.id}`}>
                      <Eye className="h-4 w-4" />
                    </Button>
                    {!readOnly && (
                      <>
                        <Button variant="ghost" size="icon" onClick={() => { setEditing(e); setDialogOpen(true); }} data-testid={`edit-employee-${e.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="hover:text-red-400" onClick={() => remove(e)} data-testid={`delete-employee-${e.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="md:hidden space-y-2" data-testid="employee-cards">
        {loading && <p className="py-8 text-center text-muted-foreground">Chargement...</p>}
        {!loading && employees.length === 0 && <p className="py-8 text-center text-muted-foreground" data-testid="employee-empty-mobile">Aucun salarié trouvé.</p>}
        {!loading && employees.map((e) => (
          <div key={e.id} className="rounded-xl border border-border bg-card p-4 space-y-2.5" data-testid={`employee-card-${e.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-semibold">{e.nom} {e.prenom}</p>
                <p className="text-xs text-muted-foreground">{e.service}</p>
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {e.ssiap?.length > 0 && (
                    <span className="inline-flex rounded-full border border-red-700 bg-red-950 px-2 py-0.5 text-[10px] font-bold text-red-400" data-testid={`ssiap-badge-m-${e.id}`}>
                      {e.ssiap.map((s) => `SSIAP ${s.niveau}`).join(" · ")}
                    </span>
                  )}
                  {e.carte_pro && (
                    <span className="inline-flex rounded-full border border-orange-700 bg-orange-950 px-2 py-0.5 text-[10px] font-bold text-orange-400" data-testid={`cartepro-badge-m-${e.id}`}>Carte Pro</span>
                  )}
                  {e.formation_incendie && (
                    <span className="inline-flex rounded-full border border-sky-700 bg-sky-950 px-2 py-0.5 text-[10px] font-bold text-sky-400" title="Équipier de Première Intervention" data-testid={`incendie-badge-m-${e.id}`}>EPI</span>
                  )}
                </div>
              </div>
              <StatutBadge statut={e.statut} joursRestants={e.jours_restants} />
            </div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs font-mono text-muted-foreground">
              <span>Naissance : {e.naissance || "—"}</span>
              <span>Formation : {e.date_formation || "—"}</span>
              <span>Fin validité : {e.fin_validite || "—"}</span>
              <span>MAC : {e.proposition_mac || "—"}{e.valide_mac === "Oui" ? " ✓" : ""}</span>
            </div>
            <div className="flex gap-2 pt-1">
              <Button variant="outline" size="sm" className="flex-1 h-10" onClick={() => setDetailEmp(e)} data-testid={`detail-employee-m-${e.id}`}>
                <Eye className="h-4 w-4 mr-1.5" /> Détails
              </Button>
              {!readOnly && (
                <>
                  <Button variant="outline" size="sm" className="flex-1 h-10" onClick={() => { setEditing(e); setDialogOpen(true); }} data-testid={`edit-employee-m-${e.id}`}>
                    <Pencil className="h-4 w-4 mr-1.5" /> Modifier
                  </Button>
                  <Button variant="outline" size="sm" className="h-10 text-red-400 hover:text-red-300" onClick={() => remove(e)} data-testid={`delete-employee-m-${e.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground" data-testid="employee-count">{employees.length} salarié(s) affiché(s)</p>

      <EmployeeDialog open={dialogOpen} onOpenChange={setDialogOpen} employee={editing} services={services.map((x) => x.nom)} onSaved={load} />
      <ServicesDialog open={servicesOpen} onOpenChange={setServicesOpen} onSaved={load} />
      <ImportDialog open={importOpen} onOpenChange={setImportOpen} onSaved={load} />
      <EmployeeDetail employee={detailEmp} open={!!detailEmp} onOpenChange={(o) => !o && setDetailEmp(null)} onSaved={load} />
    </div>
  );
}
